import React, { useState } from 'react';

const faqs = [
  {
    question: 'Who is ProfiTree for?',
    answer:
      'ProfiTree is designed for both long-term investors and active traders who manage their own portfolios. Whether you\'re a buy-and-hold investor looking to optimize tax efficiency or an active trader seeking to minimize tax impact on frequent trades, our platform provides the tools you need to maximize after-tax returns.',
    example: 'Our platform works equally well for investors who trade monthly and those who rebalance quarterly. The key is that you\'re managing your own portfolio and want to optimize your tax efficiency.'
  },
  {
    question: 'What is tax-loss harvesting and how does ProfiTree help?',
    answer:
      'Tax-loss harvesting is a strategy where you sell investments at a loss to offset taxable gains, reducing your overall tax bill. ProfiTree automatically identifies these opportunities in your portfolio and provides actionable recommendations. You maintain full control over your trades while we guide you to potential tax savings.',
    example: 'For example, if you have $5,000 in gains from selling one stock and $3,000 in losses from another, ProfiTree will show you how harvesting that loss can offset $3,000 of your gains, reducing your taxable income.'
  },
  {
    question: 'How does ProfiTree\'s guaranteed savings work?',
    answer:
      'Our Growth plan ($199/year) guarantees $2,000 minimum tax savings or you get a full refund. We\'re confident in our platform\'s ability to identify tax-saving opportunities. If you don\'t achieve the guaranteed savings, we\'ll refund your entire annual subscription - no questions asked.'
  },
  {
    question: 'What\'s the difference between your pricing tiers?',
    answer:
      'Free: For portfolios under $100K with basic tax optimization tools. Growth: $199/year for portfolios $100K-$1M with advanced features and $2,000 savings guarantee. Enterprise: Custom pricing for portfolios above $1M or financial advisors with white-label solutions and dedicated support.'
  },
  {
    question: 'How does the demo mode work?',
    answer:
      'Try our full platform with sample data to see how ProfiTree works. Click "Try Demo Mode" to access a pre-populated dashboard with realistic portfolio scenarios. Experience all features including tax-loss harvesting recommendations, wash sale detection, and tax impact calculations without linking your real accounts.'
  },
  {
    question: 'What brokerage accounts does ProfiTree support?',
    answer:
      'ProfiTree connects to major brokerages including Fidelity, Schwab, E*TRADE, Robinhood, and more through our secure SnapTrade integration. We provide read-only access to your portfolio data - we never make trades or move money. You can link multiple accounts for comprehensive tax optimization.'
  },
  {
    question: 'How secure is my financial data?',
    answer:
      'We use bank-level encryption and connect via SnapTrade (a YC-backed, Plaid-like provider). ProfiTree only has read-only access to your portfolio data and never stores your brokerage credentials. Your data is encrypted in transit and at rest, and we follow strict security protocols.'
  },
  {
    question: 'What is a wash sale and how does ProfiTree help avoid them?',
    answer:
      'A wash sale occurs when you sell a security at a loss and buy the same or substantially identical security within 30 days, which disallows the tax loss. ProfiTree tracks your trading activity and alerts you to potential wash sales, helping you maintain proper timing for maximum tax benefits.',
    example: 'If you sell shares of a stock at a loss on January 15th and buy the same stock back on February 10th (within 30 days), the IRS considers this a wash sale. ProfiTree will alert you before you make the purchase, suggesting you wait until after the 30-day window.'
  },
  {
    question: 'How accurate are the tax savings calculations?',
    answer:
      'Our calculations are based on current tax laws and your actual portfolio data. We factor in your cost basis, holding periods, and tax brackets to provide accurate estimates. However, actual tax savings depend on your specific tax situation - we recommend consulting with a tax professional for final decisions.'
  },
  {
    question: 'Can I use ProfiTree with my existing investment strategy?',
    answer:
      'Absolutely! ProfiTree works with any investment strategy. We don\'t force you into specific ETFs or change your asset allocation. Instead, we analyze your current holdings and provide recommendations to optimize tax efficiency while maintaining your investment philosophy and goals.'
  },
  {
    question: 'How do I get started with ProfiTree?',
    answer:
      'Start by trying our demo mode to see the platform in action. When ready, sign up for a free account and link your brokerage accounts securely. Our onboarding process will guide you through connecting accounts and understanding your tax optimization opportunities. You can start with the free tier and upgrade as needed.'
  },
  {
    question: 'What kind of support do you provide?',
    answer:
      'We offer comprehensive support including detailed documentation, video tutorials, and email support. Growth and Enterprise users get priority support. For complex tax questions, we recommend consulting with a tax professional, as we focus on providing the tools and insights while you make the final decisions.'
  }
];

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const styles = {
    container: {
      width: '100%',
      maxWidth: 800,
      margin: '60px auto',
      padding: '0 0 32px 0',
      background: '#f8f9fa', // match page background
    },
    title: {
      fontSize: 32,
      color: '#2a9d8f',
      fontWeight: 800,
      marginBottom: 36,
      textAlign: 'center',
      letterSpacing: 0.2,
    },
    faqItem: {
      width: '100%',
      background: 'none',
      border: 'none',
      margin: 0,
      padding: 0,
    },
    questionRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      padding: '22px 0',
    },
    question: {
      color: '#2a9d8f',
      fontWeight: 700,
      fontSize: 22,
      margin: 0,
      flex: 1,
      textAlign: 'left',
    },
    icon: {
      fontSize: 28,
      color: '#2a9d8f',
      marginLeft: 18,
      userSelect: 'none',
      fontWeight: 700,
      transition: 'transform 0.2s',
    },
    divider: {
      width: '100%',
      height: 1,
      background: '#e0e0e0',
      margin: 0,
      border: 'none',
    },
    answer: {
      color: '#222',
      fontSize: 17,
      fontWeight: 500,
      lineHeight: 1.6,
      margin: '0 0 16px 0',
      background: 'none',
    },
    example: {
      color: '#444',
      fontSize: 16,
      fontWeight: 400,
      lineHeight: 1.6,
      margin: '16px 0 0 0',
      padding: '12px 16px',
      background: 'rgba(42, 157, 143, 0.08)',
      borderLeft: '3px solid #2a9d8f',
      borderRadius: '4px',
      fontStyle: 'italic',
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Frequently Asked Questions</h2>
      {faqs.map((faq, idx) => (
        <div key={idx} style={styles.faqItem}>
          <div
            style={styles.questionRow}
            onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
            aria-expanded={openIndex === idx}
            aria-controls={`faq-answer-${idx}`}
          >
            <span style={styles.question}>{faq.question}</span>
            <span style={{ ...styles.icon, transform: openIndex === idx ? 'rotate(45deg)' : 'rotate(0deg)' }}>
              {openIndex === idx ? '−' : '+'}
            </span>
          </div>
          {openIndex === idx && (
            <div id={`faq-answer-${idx}`}>
              <div style={styles.answer}>
                {faq.answer}
              </div>
              {faq.example && (
                <div style={styles.example}>
                  {faq.example}
                </div>
              )}
            </div>
          )}
          <hr style={styles.divider} />
        </div>
      ))}
    </div>
  );
};

export default FAQ; 